import React, { useEffect } from 'react';
import { FaTimes, FaCheckCircle, FaEnvelope, FaCalendar, FaComments, FaServer, FaSpinner } from 'react-icons/fa';

const ApprovalModal = ({
    customer,
    onClose,
    onApprove,
    approvalLoading,
    approvalNotes,
    setApprovalNotes,
    serviceActivationDate,
    setServiceActivationDate,
    // New: Vocus identifiers captured at approval time so the customer record
    // is complete before billing kicks off. AVC ID is required for any
    // FIBRE::Create downstream (whether we provision via admin portal or
    // copy-paste into the Vocus portal manually).
    vocusServiceId,
    setVocusServiceId,
    avcId,
    setAvcId,
}) => {
    // Pre-fill from the customer record if previously saved (e.g. retry approval flow).
    useEffect(() => {
        if (!avcId && customer?.avcId) setAvcId(customer.avcId);
        // serviceId may live under the vocusOrderSnapshot if provisioned via admin portal.
        if (!vocusServiceId && customer?.vocusOrderSnapshot?.serviceId) {
            setVocusServiceId(customer.vocusOrderSnapshot.serviceId);
        }
    }, [customer?.UID]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#282828] rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col relative">
                {/* Full-modal lock overlay while the approval round-trip is in flight.
                    Belt to the ref-based guard's braces: even if the underlying button
                    re-enables for a frame, every click target inside the modal is
                    physically covered so admin can't accidentally fire anything else
                    (close, edit a field, re-submit). Stripe charge stays single. */}
                {approvalLoading && (
                    <div
                        className="absolute inset-0 bg-[#1d2021] bg-opacity-80 rounded-xl z-10 flex items-center justify-center cursor-wait"
                        // Capture-phase click swallower in case anything below tries to react.
                        onClickCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    >
                        <div className="flex flex-col items-center gap-3 text-[#ebdbb2]">
                            <FaSpinner className="animate-spin text-[#fabd2f]" size={32} />
                            <p className="text-lg font-semibold">Approving customer…</p>
                            <p className="text-sm text-[#928374]">Do not close — Stripe charge in progress</p>
                        </div>
                    </div>
                )}
                <div className="p-6 border-b border-[#3c3836] flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold text-[#ebdbb2]">Approve Customer</h2>
                        <button
                            onClick={onClose}
                            className="text-[#928374] hover:text-[#bdae93]"
                            disabled={approvalLoading}
                        >
                            <FaTimes size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {/* Customer Info */}
                    <div className="mb-6 p-4 bg-[#3c3836] rounded-lg">
                        <h3 className="text-lg font-semibold text-[#ebdbb2] mb-3">Customer Information</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="font-medium text-[#bdae93]">Name:</span>
                                <p className="text-[#ebdbb2]">{customer.fullName}</p>
                            </div>
                            <div>
                                <span className="font-medium text-[#bdae93]">Email:</span>
                                <p className="text-[#ebdbb2]">{customer.email}</p>
                            </div>
                            <div>
                                <span className="font-medium text-[#bdae93]">Plan:</span>
                                <p className="text-[#ebdbb2]">{customer.plan} - ${customer.monthlyFee}/month</p>
                            </div>
                            <div>
                                <span className="font-medium text-[#bdae93]">Account Number:</span>
                                <p className="text-[#ebdbb2]">{customer.accountNumber}</p>
                            </div>
                        </div>
                    </div>

                    {/* Approval Actions */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-[#ebdbb2] mb-3">Approval Actions</h3>
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <FaCheckCircle className="text-green-600" />
                                <span className="text-sm text-green-800">✓ Change status to "approved"</span>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <FaEnvelope className="text-blue-600" />
                                <span className="text-sm text-blue-800">✓ Send welcome email with activation link</span>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                <FaCalendar className="text-purple-600" />
                                <span className="text-sm text-purple-800">✓ Set service activation date and billing cycle start</span>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                <FaComments className="text-orange-600" />
                                <span className="text-sm text-orange-800">✓ Add approval notes (required)</span>
                            </div>
                        </div>
                    </div>

                    {/* Approval Notes */}
                    <div className="mb-6">
                        <label htmlFor="approvalNotes" className="block text-sm font-medium text-[#bdae93] mb-2">
                            Approval Notes <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="approvalNotes"
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            placeholder="Enter approval notes and any special instructions..."
                            className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                            rows={4}
                            disabled={approvalLoading}
                        />
                        <p className="text-xs text-[#928374] mt-1">
                            These notes will be recorded with the approval and may be included in the welcome email.
                        </p>
                    </div>

                    {/* Service Activation Date */}
                    <div className="mb-6">
                        <label htmlFor="serviceActivationDate" className="block text-sm font-medium text-[#bdae93] mb-2">
                            Service Activation Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="serviceActivationDate"
                            type="date"
                            value={serviceActivationDate}
                            onChange={(e) => setServiceActivationDate(e.target.value)}
                            className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                            disabled={approvalLoading}
                            required
                        />
                        <p className="text-xs text-[#928374] mt-1">
                            The date when the customer's service will be activated. Defaults to today.
                        </p>
                    </div>

                    {/* Vocus identifiers — captured at approval so the customer record is
                        complete before the first billing cycle. AVC ID is mandatory;
                        Vocus Service ID is required when provisioning via the Vocus portal
                        manually (without it we can't action cancellations/moves later). */}
                    <div className="mb-6 p-4 bg-[#3c3320] border border-[#d79921] rounded-lg space-y-4">
                        <div className="flex items-center gap-2">
                            <FaServer className="text-[#fabd2f]" />
                            <h3 className="text-sm font-semibold text-[#fabd2f]">Vocus Service Details</h3>
                        </div>
                        <div>
                            <label htmlFor="avcId" className="block text-sm font-medium text-[#bdae93] mb-2">
                                AVC ID <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="avcId"
                                type="text"
                                value={avcId || ''}
                                onChange={(e) => setAvcId(e.target.value.trim())}
                                placeholder="e.g. AVC000123456"
                                className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent font-mono"
                                disabled={approvalLoading}
                            />
                            <p className="text-xs text-[#928374] mt-1">
                                The NBN AVC identifier from Vocus. Required so billing + future cancel/move actions can target the right service.
                            </p>
                        </div>
                        <div>
                            <label htmlFor="vocusServiceId" className="block text-sm font-medium text-[#bdae93] mb-2">
                                Vocus Service ID <span className="text-[#928374] text-xs">(optional if provisioning later)</span>
                            </label>
                            <input
                                id="vocusServiceId"
                                type="text"
                                value={vocusServiceId || ''}
                                onChange={(e) => setVocusServiceId(e.target.value.trim())}
                                placeholder="e.g. SVC0123456 or auto-filled after admin-portal Provision"
                                className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent font-mono"
                                disabled={approvalLoading}
                            />
                            <p className="text-xs text-[#928374] mt-1">
                                The Vocus service ID returned after FIBRE::Create. Paste it here if you provisioned via the Vocus portal directly.
                            </p>
                        </div>
                    </div>

                    {/* Billing Information */}
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h3 className="text-sm font-semibold text-blue-900 mb-2">Billing Information</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="font-medium text-blue-700">Billing Start Date:</span>
                                <p className="text-blue-900">{new Date().toLocaleDateString()}</p>
                            </div>
                            <div>
                                <span className="font-medium text-blue-700">First Billing Date:</span>
                                <p className="text-blue-900">{new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                            </div>
                            <div>
                                <span className="font-medium text-blue-700">Monthly Fee:</span>
                                <p className="text-blue-900">${customer.monthlyFee}</p>
                            </div>
                            <div>
                                <span className="font-medium text-blue-700">Plan:</span>
                                <p className="text-blue-900">{customer.plan}</p>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end space-x-3 pt-4 border-t border-[#3c3836] mt-6">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-[#504945] text-[#bdae93] rounded-lg hover:bg-[#3c3836]"
                            disabled={approvalLoading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onApprove}
                            disabled={approvalLoading || !approvalNotes.trim() || !(avcId && avcId.trim())}
                            title={!avcId || !avcId.trim() ? 'AVC ID is required to approve' : ''}
                            className="px-6 py-2 bg-[#b8bb26] text-[#1d2021] rounded-lg hover:bg-[#98971a] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {approvalLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Approving...</span>
                                </>
                            ) : (
                                <>
                                    <FaCheckCircle />
                                    <span>Approve Customer</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApprovalModal;
